import {DEFAULT_DATASET} from '@apps-common/geocorn-sdk/api/datasets';
import {
  deleteFeature,
  getFeatureByReference,
  updateFeature,
} from '@apps-common/geocorn-sdk/api/features';
import {processAPIInstance} from '@apps-common/geocorn-sdk/api/index.js';
import {getFeatureReference} from '@apps-common/geocorn-sdk/api/util';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {queryKeys as collectionsQueryKeys} from './collections.js';
import {graneroPager, removeEmpty} from './util.js';
import {getHashFromId} from '@app/util/granero';

// ToDo: Promote to geocorn-sdk once stable
// Uses custom "pager" to be able to get all features
const getFeatures = (collectionId, options) => {
  const {dataset = DEFAULT_DATASET, params, ...otherOptions} = options || {};

  const config = {
    url: `/ogc/${dataset}/collections/${collectionId}/items/`,
    params: {...params},
    ...otherOptions,
  };

  if (!config.params.limit) {
    config.params.limit = 100; // default so we can bail early on incomplete pages
  }

  if (!config.maxItems) {
    config.maxItems = config.params.limit;
  }

  return graneroPager(config, 'features', config.each);
};

export const queryKeys = {
  all: ['features'],
  lists: () => [...queryKeys.all, 'list'],
  list: params => [...queryKeys.lists(), params],
  details: () => [...queryKeys.all, 'details'],
  detail: id => [...queryKeys.all, 'details', id],
};

export const useGetFeatures = (
  {collectionId, params, ...options} = {},
  queryOptions = {}
) => {
  const queryParams = removeEmpty({
    //ToDo: Bring back once CSS-532 is resolved
    // _view: 'basic',
    '_x-lock': true,
    ...params,
  });
  return useQuery({
    queryKey: queryKeys.list({
      collectionId: getHashFromId(collectionId),
      ...queryParams,
      ...options,
    }),
    queryFn: () => getFeatures(collectionId, {params: queryParams, ...options}),
    ...queryOptions,
  });
};

export const useGetFeatureByReference = (
  {reference} = {},
  queryOptions = {}
) => {
  return useQuery({
    queryKey: queryKeys.list(reference),
    queryFn: () => getFeatureByReference(reference),
    ...queryOptions,
  });
};

export const useUpdateFeature = (queryOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({feature}) => updateFeature(feature),
    ...queryOptions,
    onSuccess: (data, args) => {
      queryClient.invalidateQueries({queryKey: queryKeys.lists()});
      if (queryOptions.onSuccess) {
        queryOptions.onSuccess(data, args);
      }
    },
  });
};

export const useDeleteFeature = (queryOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({feature}) => {
      const featureRef = getFeatureReference(feature);
      return deleteFeature(featureRef);
    },
    ...queryOptions,
    onSuccess: (data, args) => {
      if (queryOptions.onSuccess) {
        queryOptions.onSuccess(data, args);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({queryKey: queryKeys.lists()});
      queryClient.invalidateQueries({queryKey: collectionsQueryKeys.lists()});
    },
  });
};

const withinBoundary = ({boundary, ref}) => {
  const config = {
    url: `${processAPIInstance.defaults.baseURL}/process/within_boundary/execution`,
    method: 'POST',
    data: {boundary, refs: [ref]},
  };

  return graneroPager(config, 'refs', config.each);
};

export const useWithinBoundary = ({boundary, ref}, queryOptions = {}) =>
  useQuery({
    queryKey: ['withinBoundary', boundary, ref],
    queryFn: () => withinBoundary({boundary, ref}),
    select: data => {
      const response = data.refs.reduce(
        (accumulator, ref) => {
          const hashId = getHashFromId(ref.ref);
          if (ref.is_within) {
            accumulator.refsInsideBoundary.push(ref.ref);
            accumulator.hashIdsInsideBoundary.push(hashId);
          } else {
            accumulator.refsOutsideBoundary.push(ref.ref);
            accumulator.hashIdsOutsideBoundary.push(hashId);
          }
          return accumulator;
        },
        {
          refsInsideBoundary: [],
          refsOutsideBoundary: [],
          hashIdsInsideBoundary: [],
          hashIdsOutsideBoundary: [],
        }
      );

      return {
        ...data,
        ...response,
      };
    },
    staleTime: 5 * 60 * 1000,
    ...queryOptions,
  });
